import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';

import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import ProductOrder from '@/shared/types/productOrder.type';

import transformVariantsAndProducts from '../utils/transformVariantsAndProducts';
import getSearchData from './getSearchData';

declare global {
  interface USBDevice {
    open: () => Promise<void>;
  }
  interface Navigator {
    usb: {
      requestDevice: (options: { filters: [] }) => Promise<USBDevice>;
      getDevices: () => Promise<USBDevice[]>;
    };
  }
}

const SCANNED_DEBOUNCE_TIME = 500;

const useScanningDevice = () => {
  const redirectLogin = useRedirectLogin();

  const [scannedBarcode, setScannedBarcode] = useState('');
  const [lastKeyPressTime, setLastKeyPressTime] = useState(10);
  const [isConnectedUsb, setIsConnectedUsb] = useState(false);
  const [product, setProduct] = useState<ProductOrder | null>(null);
  const [searchingStatus, setSearchingStatus] = useState<
    'idle' | 'searching' | 'complete' | 'failed'
  >('idle');

  const scannedBarcodeDebounced = useDebounce(
    scannedBarcode,
    SCANNED_DEBOUNCE_TIME,
  );

  const onClearBarCode = () => {
    setTimeout(() => setScannedBarcode(''), SCANNED_DEBOUNCE_TIME + 100);
  };

  useEffect(() => {
    const fetchData = async () => {
      setSearchingStatus('searching');

      const { products, variants } = await getSearchData({
        searchInputValue: scannedBarcodeDebounced,
        filterBy: ['bar_code'],
        cop: ['like'],
        source: 'barcodeScanner',
        redirectLogin,
      });

      if (products && variants) {
        const selectedProducts = transformVariantsAndProducts({
          products,
          variants,
        });

        setProduct(selectedProducts[0]);
        setSearchingStatus('complete');
      } else {
        setSearchingStatus('failed');
      }
    };

    if (scannedBarcodeDebounced) {
      void fetchData();
    }
  }, [redirectLogin, scannedBarcodeDebounced]);

  const onConnectUsb = async () => {
    try {
      const device = await navigator.usb.requestDevice({ filters: [] });
      await device.open();

      setIsConnectedUsb(true);
    } catch (error) {
      console.error('Failed to connect to the scanner:', error);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isConnectedUsb) return;

      const timeDifference = event.timeStamp - lastKeyPressTime;
      setLastKeyPressTime(event.timeStamp);

      if (timeDifference < 30) {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }

        setScannedBarcode((prev) => (prev + event.key).replace('Enter', ''));

        if (event.key === 'Enter') {
          if (scannedBarcode) {
            onClearBarCode();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isConnectedUsb, lastKeyPressTime, scannedBarcode]);

  useEffect(() => {
    navigator.usb
      .getDevices()
      .then((devices) => {
        if (devices.length > 0) {
          setIsConnectedUsb(true);
        }
      })
      .catch((error) => {
        console.error('Failed to get connected USB devices:', error);
      });
  }, []);

  useEffect(() => {
    if (searchingStatus === 'complete') {
      setProduct(null);
      setSearchingStatus('idle');
    }
  }, [searchingStatus]);

  return {
    product,
    isConnectedUsb,
    searchingStatus,
    onConnectUsb,
  };
};

export default useScanningDevice;
